import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Upload,
  ScanSearch,
  Loader2,
  CheckCircle2,
  Sparkles,
  Info,
  Save,
  ArrowRight,
  RotateCcw,
  Layers,
  FileImage,
  Check,
  Plus,
  Trash2,
  Move,
  Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import HUDPageHeader from '../components/HUDPageHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

const SmartExtractorPage = () => {
  const { user } = useAuth();
  // Phase: upload | analyzing | results
  const [phase, setPhase] = useState('upload');
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [prepId, setPrepId] = useState(null);
  const [analyzeProgress, setAnalyzeProgress] = useState(0);
  const [analyzeText, setAnalyzeText] = useState('');

  // Source info form
  const [sourceInfo, setSourceInfo] = useState(() => {
    const saved = localStorage.getItem('extractor_source_info');
    return saved ? JSON.parse(saved) : { project: '', tranche: '', building: '', floor: '' };
  });
  const [memorize, setMemorize] = useState(() => localStorage.getItem('extractor_memorize') === 'true');

  // Detected lots
  const [detectedLots, setDetectedLots] = useState([]);
  const [importing, setImporting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [targetProject, setTargetProject] = useState('');
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  // Interactive editing
  const [selectedLotId, setSelectedLotId] = useState(null);
  const [drawingNew, setDrawingNew] = useState(false);
  const [editingNameId, setEditingNameId] = useState(null);
  const dragRef = useRef({ type: null, startMouse: null, startBox: null, lotId: null });

  useEffect(() => {
    axios.get(`${API}/projects`, { withCredentials: true }).then(r => setProjects(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (memorize) {
      localStorage.setItem('extractor_source_info', JSON.stringify(sourceInfo));
      localStorage.setItem('extractor_memorize', 'true');
    } else {
      localStorage.removeItem('extractor_memorize');
    }
  }, [memorize, sourceInfo]);

  const buildLotName = (detectedName) => {
    const parts = [sourceInfo.project, sourceInfo.tranche, sourceInfo.building, sourceInfo.floor, detectedName].filter(Boolean);
    return parts.length > 1 ? parts.join(' - ') : detectedName;
  };

  const handleUpload = async (file) => {
    if (!file) return;
    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowed.includes(file.type) && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Format non supporté. Utilisez PDF, PNG ou JPG.');
      return;
    }
    setUploadedFile(file);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/plan-prep/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPrepId(res.data.prep_id);
      setImageUrl(`${API}/plan-prep/${res.data.prep_id}/image?t=${Date.now()}`);
      toast.success('Plan chargé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur upload');
      setUploadedFile(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  };

  const handleAnalyze = async () => {
    if (!prepId) return;
    setPhase('analyzing');
    setAnalyzeProgress(0);

    // Animated progress while AI works
    const progressSteps = [
      { text: 'Envoi du plan à l\'IA de détection...', target: 15 },
      { text: 'Détection des murs et cloisons...', target: 30 },
      { text: 'Lecture des labels et annotations...', target: 50 },
      { text: 'Identification des lots individuels...', target: 70 }
    ];

    let progressInterval;
    let currentStep = 0;
    const runProgress = () => {
      progressInterval = setInterval(() => {
        if (currentStep < progressSteps.length) {
          setAnalyzeText(progressSteps[currentStep].text);
          setAnalyzeProgress(progressSteps[currentStep].target);
          currentStep++;
        }
      }, 2500);
    };
    runProgress();

    try {
      // Call real AI detection endpoint
      const res = await axios.post(`${API}/plan-prep/${prepId}/ai-detect`, {}, {
        withCredentials: true,
        timeout: 60000
      });
      clearInterval(progressInterval);

      const aiLots = res.data.lots || [];
      if (aiLots.length === 0) {
        toast.error('L\'IA n\'a détecté aucun lot dans ce plan.');
        setPhase('upload');
        return;
      }

      setAnalyzeText('Extraction et recadrage des zones...');
      setAnalyzeProgress(90);
      await new Promise(r => setTimeout(r, 300));

      const generated = aiLots.map((lot, i) => ({
        id: `det_${Date.now()}_${i}`,
        detectedName: lot.name,
        fullName: buildLotName(lot.name),
        box: { x: lot.x, y: lot.y, w: lot.w, h: lot.h },
        color: COLORS[i % COLORS.length],
        checked: true,
        typology: lot.typology || '?',
        surface: 0
      }));

      setAnalyzeProgress(100);
      setAnalyzeText('Analyse terminée !');
      await new Promise(r => setTimeout(r, 400));

      setDetectedLots(generated);
      setPhase('results');
      toast.success(`${generated.length} lot(s) détecté(s) par l'IA`);
    } catch (error) {
      clearInterval(progressInterval);
      const msg = error.response?.data?.detail || 'Erreur lors de la détection IA';
      toast.error(msg);
      setPhase('upload');
    }
  };

  const handleImport = async () => {
    const selected = detectedLots.filter(l => l.checked);
    if (selected.length === 0) {
      toast.error('Sélectionnez au moins un lot');
      return;
    }
    if (!targetProject) {
      toast.error('Sélectionnez un projet cible');
      return;
    }
    setImporting(true);
    try {
      // Use existing segment endpoint
      const rectangles = selected.map(l => ({
        x: l.box.x * 10,
        y: l.box.y * 10,
        width: l.box.w * 10,
        height: l.box.h * 10,
        name: l.fullName
      }));

      await axios.post(`${API}/plan-prep/${prepId}/segment`, {
        project_id: targetProject,
        rectangles,
        canvasWidth: 1000,
        canvasHeight: 1000
      }, { withCredentials: true });

      toast.success(`${selected.length} lots importés avec succès !`);
      // Don't reset memorized info
      if (!memorize) {
        setSourceInfo({ project: '', tranche: '', building: '', floor: '' });
      }
      setPhase('upload');
      setUploadedFile(null);
      setImageUrl(null);
      setPrepId(null);
      setDetectedLots([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur import');
    } finally {
      setImporting(false);
    }
  };

  const resetAll = () => {
    setPhase('upload');
    setUploadedFile(null);
    setImageUrl(null);
    setPrepId(null);
    setDetectedLots([]);
    setAnalyzeProgress(0);
  };

  const selectedCount = detectedLots.filter(l => l.checked).length;

  // Get mouse position as % of canvas
  const getPercent = useCallback((e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return null;
    // Support both mouse and touch events
    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? e.changedTouches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? e.changedTouches?.[0]?.clientY;
    if (clientX == null || clientY == null) return null;
    return {
      x: ((clientX - rect.left) / rect.width) * 100,
      y: ((clientY - rect.top) / rect.height) * 100
    };
  }, []);

  // Canvas mouse handlers for move/resize/draw
  const handleCanvasMouseDown = useCallback((e) => {
    const pct = getPercent(e);
    if (!pct) return;

    // Drawing new box mode
    if (drawingNew) {
      dragRef.current = { type: 'draw', startMouse: pct, startBox: null, lotId: null };
      return;
    }

    // Check if clicking on a resize handle (bigger threshold for touch)
    const sel = detectedLots.find(l => l.id === selectedLotId);
    if (sel) {
      const handles = [
        { name: 'nw', cx: sel.box.x, cy: sel.box.y },
        { name: 'ne', cx: sel.box.x + sel.box.w, cy: sel.box.y },
        { name: 'sw', cx: sel.box.x, cy: sel.box.y + sel.box.h },
        { name: 'se', cx: sel.box.x + sel.box.w, cy: sel.box.y + sel.box.h }
      ];
      const canvasRect = canvasRef.current.getBoundingClientRect();
      const isTouch = e.type && e.type.startsWith('touch');
      const thresholdPx = isTouch ? 22 : 10;
      const threshold = thresholdPx / canvasRect.width * 100;
      for (const h of handles) {
        if (Math.abs(pct.x - h.cx) < threshold && Math.abs(pct.y - h.cy) < threshold) {
          dragRef.current = { type: `resize-${h.name}`, startMouse: pct, startBox: { ...sel.box }, lotId: sel.id };
          if (e.preventDefault) e.preventDefault();
          return;
        }
      }
    }

    // Check if clicking inside any box → select & start move
    for (let i = detectedLots.length - 1; i >= 0; i--) {
      const lot = detectedLots[i];
      if (pct.x >= lot.box.x && pct.x <= lot.box.x + lot.box.w &&
          pct.y >= lot.box.y && pct.y <= lot.box.y + lot.box.h) {
        setSelectedLotId(lot.id);
        dragRef.current = { type: 'move', startMouse: pct, startBox: { ...lot.box }, lotId: lot.id };
        if (e.preventDefault) e.preventDefault();
        return;
      }
    }

    // Click on empty area → deselect
    setSelectedLotId(null);
  }, [detectedLots, selectedLotId, drawingNew, getPercent]);

  const handleCanvasMouseMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d.type) return;
    const pct = getPercent(e);
    if (!pct) return;
    const dx = pct.x - d.startMouse.x;
    const dy = pct.y - d.startMouse.y;

    if (d.type === 'draw') {
      // Update a temporary new box
      const newBox = {
        x: Math.min(d.startMouse.x, pct.x),
        y: Math.min(d.startMouse.y, pct.y),
        w: Math.abs(pct.x - d.startMouse.x),
        h: Math.abs(pct.y - d.startMouse.y)
      };
      setDetectedLots(prev => {
        const temp = prev.filter(l => l.id !== '__drawing__');
        return [...temp, {
          id: '__drawing__',
          detectedName: `Lot ${prev.filter(l => l.id !== '__drawing__').length + 1}`,
          fullName: buildLotName(`Lot ${prev.filter(l => l.id !== '__drawing__').length + 1}`),
          box: newBox,
          color: COLORS[prev.filter(l => l.id !== '__drawing__').length % COLORS.length],
          checked: true, typology: '?', surface: 0
        }];
      });
      return;
    }

    setDetectedLots(prev => prev.map(lot => {
      if (lot.id !== d.lotId) return lot;
      let newBox;
      if (d.type === 'move') {
        newBox = {
          x: Math.max(0, Math.min(100 - d.startBox.w, d.startBox.x + dx)),
          y: Math.max(0, Math.min(100 - d.startBox.h, d.startBox.y + dy)),
          w: d.startBox.w,
          h: d.startBox.h
        };
      } else if (d.type === 'resize-se') {
        newBox = { x: d.startBox.x, y: d.startBox.y,
          w: Math.max(2, d.startBox.w + dx), h: Math.max(2, d.startBox.h + dy) };
      } else if (d.type === 'resize-nw') {
        newBox = {
          x: Math.max(0, d.startBox.x + dx), y: Math.max(0, d.startBox.y + dy),
          w: Math.max(2, d.startBox.w - dx), h: Math.max(2, d.startBox.h - dy)
        };
      } else if (d.type === 'resize-ne') {
        newBox = {
          x: d.startBox.x, y: Math.max(0, d.startBox.y + dy),
          w: Math.max(2, d.startBox.w + dx), h: Math.max(2, d.startBox.h - dy)
        };
      } else if (d.type === 'resize-sw') {
        newBox = {
          x: Math.max(0, d.startBox.x + dx), y: d.startBox.y,
          w: Math.max(2, d.startBox.w - dx), h: Math.max(2, d.startBox.h + dy)
        };
      }
      return newBox ? { ...lot, box: newBox } : lot;
    }));
  }, [getPercent, buildLotName]);

  const handleCanvasMouseUp = useCallback(() => {
    const d = dragRef.current;
    if (d.type === 'draw') {
      // Finalize the drawn box
      setDetectedLots(prev => {
        const drawing = prev.find(l => l.id === '__drawing__');
        if (!drawing || drawing.box.w < 2 || drawing.box.h < 2) {
          return prev.filter(l => l.id !== '__drawing__');
        }
        const finalId = `det_${Date.now()}`;
        const count = prev.filter(l => l.id !== '__drawing__').length + 1;
        return prev.map(l => l.id === '__drawing__' ? {
          ...l, id: finalId,
          detectedName: `Lot ${count}`,
          fullName: buildLotName(`Lot ${count}`)
        } : l);
      });
      setDrawingNew(false);
    }
    dragRef.current = { type: null, startMouse: null, startBox: null, lotId: null };
  }, [buildLotName]);

  const deleteSelectedLot = useCallback(() => {
    if (!selectedLotId) return;
    setDetectedLots(prev => prev.filter(l => l.id !== selectedLotId));
    setSelectedLotId(null);
    toast.success('Lot supprimé');
  }, [selectedLotId]);

  const addNewLot = useCallback(() => {
    setDrawingNew(true);
    setSelectedLotId(null);
    toast.info('Dessinez un rectangle sur le plan');
  }, []);

  return (
    <div data-hud-page data-testid="smart-extractor-page" className="fade-in">
      <HUDPageHeader
        module="Cockpit"
        breadcrumb={[{ label: 'Hub', to: '/studio/dashboard' }, { label: 'Extracteur IA' }]}
        icon={ScanSearch}
        title="Extracteur de Lots"
        subtitle="Analysez un plan d'ensemble et extrayez automatiquement chaque lot"
        actions={phase !== 'upload' && (
          <button
            onClick={resetAll}
            data-testid="reset-extractor"
            className="hud-btn hud-btn-secondary hud-btn-md"
          >
            <RotateCcw className="w-4 h-4" />Nouveau plan
          </button>
        )}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-6">

      {/* ============ PHASE: UPLOAD ============ */}
      {phase === 'upload' && (
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Source Info Form */}
          <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="source-info-form">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <Info className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Informations du Plan Source</h3>
                <p className="text-xs text-gray-500">Contextualisez le document pour nommer automatiquement les lots extraits</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Projet / Programme</label>
                <Input
                  value={sourceInfo.project}
                  onChange={(e) => setSourceInfo({ ...sourceInfo, project: e.target.value })}
                  placeholder="Rés. Les Oliviers"
                  className="h-9 text-sm"
                  data-testid="source-project"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Tranche / Lot Global</label>
                <Input
                  value={sourceInfo.tranche}
                  onChange={(e) => setSourceInfo({ ...sourceInfo, tranche: e.target.value })}
                  placeholder="GH15"
                  className="h-9 text-sm"
                  data-testid="source-tranche"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Bâtiment / Immeuble</label>
                <Input
                  value={sourceInfo.building}
                  onChange={(e) => setSourceInfo({ ...sourceInfo, building: e.target.value })}
                  placeholder="Bâtiment 10"
                  className="h-9 text-sm"
                  data-testid="source-building"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Étage / Niveau</label>
                <Input
                  value={sourceInfo.floor}
                  onChange={(e) => setSourceInfo({ ...sourceInfo, floor: e.target.value })}
                  placeholder="RDC"
                  className="h-9 text-sm"
                  data-testid="source-floor"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={memorize}
                onChange={(e) => setMemorize(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                data-testid="memorize-checkbox"
              />
              <span className="text-xs text-gray-600">Mémoriser ces informations pour le prochain fichier</span>
              <Save className="w-3 h-3 text-gray-400" />
            </label>
          </div>

          {/* Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-2xl p-10 sm:p-16 text-center transition-all cursor-pointer ${
              dragOver ? 'border-blue-500 bg-blue-50 scale-[1.01]' : 
              uploadedFile ? 'border-green-400 bg-green-50/50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/30'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => !uploadedFile && document.getElementById('extractor-upload').click()}
            data-testid="extractor-dropzone"
          >
            <input
              id="extractor-upload"
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp"
              className="hidden"
              onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0])}
            />
            {uploadedFile ? (
              <div className="space-y-3">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
                <p className="text-lg font-medium text-gray-900">{uploadedFile.name}</p>
                <p className="text-sm text-green-600">Plan chargé et prêt pour l'analyse</p>
                {imageUrl && (
                  <div className="mt-4 max-w-md mx-auto rounded-lg overflow-hidden border border-gray-200">
                    <img src={imageUrl} alt="Aperçu" className="w-full h-auto" crossOrigin="use-credentials" />
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <ScanSearch className="w-8 h-8 text-blue-600" />
                </div>
                <p className="text-lg font-medium text-gray-900 mb-2">Déposez ici le plan de masse ou le plan d'étage complet</p>
                <p className="text-sm text-gray-500 mb-1">Fichier global contenant plusieurs lots à extraire</p>
                <p className="text-xs text-gray-400">PDF, PNG, JPG — max 50 Mo</p>
              </>
            )}
          </div>

          {/* Analyze Button */}
          {uploadedFile && prepId && (
            <div className="text-center">
              <Button
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-6 text-base rounded-xl shadow-lg shadow-blue-200/50 transition-all hover:shadow-xl hover:shadow-blue-300/50"
                onClick={handleAnalyze}
                data-testid="launch-analysis-btn"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Lancer l'Analyse IA & Extraire les Lots
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ============ PHASE: ANALYZING ============ */}
      {phase === 'analyzing' && (
        <div className="max-w-lg mx-auto text-center py-16">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 bg-blue-100 rounded-full animate-ping opacity-30" />
            <div className="relative w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-white animate-spin" />
            </div>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Analyse en cours</h2>
          <p className="text-sm text-gray-600 mb-6 min-h-[20px]">{analyzeText}</p>
          <Progress value={analyzeProgress} className="h-2 max-w-sm mx-auto mb-2" />
          <p className="text-xs text-gray-400">{analyzeProgress}%</p>
        </div>
      )}

      {/* ============ PHASE: RESULTS ============ */}
      {phase === 'results' && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4 lg:gap-6" data-testid="results-view">
          {/* Canvas with interactive bounding boxes */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-3 sm:px-4 py-2.5 border-b border-gray-100 flex items-center gap-2 flex-wrap">
              <FileImage className="w-4 h-4 text-gray-400 shrink-0" />
              <span className="text-xs sm:text-sm font-medium text-gray-700 truncate">Plan analysé — {detectedLots.filter(l => l.id !== '__drawing__').length} lots</span>
              <div className="ml-auto flex items-center gap-1.5 shrink-0">
                <Button size="sm" variant={drawingNew ? 'default' : 'outline'} className="h-7 text-xs gap-1"
                  onClick={addNewLot} data-testid="add-lot-btn">
                  <Plus className="w-3 h-3" />Ajouter
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-red-500 hover:bg-red-50"
                  onClick={deleteSelectedLot} disabled={!selectedLotId} data-testid="delete-lot-btn">
                  <Trash2 className="w-3 h-3" />Supprimer
                </Button>
              </div>
            </div>
            <div
              ref={canvasRef}
              className={`relative select-none touch-none ${drawingNew ? 'cursor-crosshair' : 'cursor-default'}`}
              onMouseDown={handleCanvasMouseDown}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
              onMouseLeave={handleCanvasMouseUp}
              onTouchStart={handleCanvasMouseDown}
              onTouchMove={handleCanvasMouseMove}
              onTouchEnd={handleCanvasMouseUp}
              onTouchCancel={handleCanvasMouseUp}
              data-testid="analysis-canvas"
            >
              {imageUrl && (
                <img ref={imgRef} src={imageUrl} alt="Plan" crossOrigin="use-credentials"
                  className="w-full h-auto block pointer-events-none" draggable={false} />
              )}
              {/* Bounding boxes */}
              {detectedLots.map((lot, i) => {
                const isSelected = lot.id === selectedLotId;
                const isDrawing = lot.id === '__drawing__';
                return (
                  <div
                    key={lot.id}
                    className={`absolute transition-opacity ${lot.checked || isDrawing ? 'opacity-100' : 'opacity-30'} ${isSelected ? 'z-20' : 'z-10'}`}
                    style={{
                      left: `${lot.box.x}%`, top: `${lot.box.y}%`,
                      width: `${lot.box.w}%`, height: `${lot.box.h}%`,
                      borderWidth: isSelected ? 3 : 2,
                      borderStyle: isDrawing ? 'dashed' : 'solid',
                      borderColor: lot.color,
                      backgroundColor: isSelected ? `${lot.color}20` : `${lot.color}10`,
                      cursor: isSelected ? 'move' : 'pointer'
                    }}
                    data-testid={isDrawing ? 'bbox-drawing' : `bbox-${i}`}
                  >
                    {/* Label */}
                    <span className="absolute -top-5 left-1 text-[10px] font-bold text-white px-1.5 py-0.5 rounded whitespace-nowrap pointer-events-none"
                      style={{ backgroundColor: lot.color }}>
                      {lot.detectedName}
                    </span>
                    {/* Type/surface */}
                    {!isDrawing && (
                      <span className="absolute bottom-1 right-1 text-[9px] text-white/90 bg-black/40 px-1 py-0.5 rounded pointer-events-none">
                        {lot.typology}
                      </span>
                    )}
                    {/* Resize handles */}
                    {isSelected && !isDrawing && (
                      <>
                        {['nw','ne','sw','se'].map(pos => (
                          <div key={pos} className="absolute w-4 h-4 sm:w-3 sm:h-3 bg-white border-2 rounded-sm z-30 shadow-md"
                            style={{
                              borderColor: lot.color,
                              ...(pos.includes('n') ? { top: -8 } : { bottom: -8 }),
                              ...(pos.includes('w') ? { left: -8 } : { right: -8 }),
                              cursor: `${pos}-resize`,
                              touchAction: 'none'
                            }}
                            data-testid={`handle-${pos}`}
                          />
                        ))}
                      </>
                    )}
                  </div>
                );
              })}
              {/* Drawing mode indicator */}
              {drawingNew && !dragRef.current?.type && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
                  <div className="bg-blue-600 text-white text-xs px-3 py-1.5 rounded-full font-medium animate-pulse">
                    Dessinez un rectangle
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right panel */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-blue-600" />
                  Lots Détectés
                  <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    {detectedLots.filter(l => l.id !== '__drawing__').length}
                  </span>
                </h3>
              </div>
              <div className="max-h-[400px] overflow-y-auto divide-y divide-gray-50">
                {detectedLots.filter(l => l.id !== '__drawing__').map((lot, i) => {
                  const isSelected = lot.id === selectedLotId;
                  return (
                    <div
                      key={lot.id}
                      className={`p-3 transition-colors cursor-pointer ${isSelected ? 'bg-blue-50 ring-1 ring-blue-200' : lot.checked ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 opacity-60'}`}
                      onClick={() => setSelectedLotId(lot.id)}
                      data-testid={`detected-lot-${i}`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={lot.checked}
                          onChange={(e) => {
                            e.stopPropagation();
                            const updated = detectedLots.map(l => l.id === lot.id ? { ...l, checked: e.target.checked } : l);
                            setDetectedLots(updated);
                          }}
                          className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          data-testid={`lot-check-${i}`}
                        />
                        <div className="w-12 h-12 rounded-lg border border-gray-200 overflow-hidden shrink-0 bg-gray-100">
                          {imageUrl && (
                            <div className="w-full h-full relative overflow-hidden">
                              <img src={imageUrl} alt={lot.detectedName} crossOrigin="use-credentials"
                                className="absolute" style={{ left: `-${lot.box.x * 2}%`, top: `-${lot.box.y * 2}%`, width: '250%', maxWidth: 'none' }} />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          {editingNameId === lot.id ? (
                            <Input
                              autoFocus
                              defaultValue={lot.detectedName}
                              className="h-6 text-xs mb-1"
                              onBlur={(e) => {
                                const val = e.target.value.trim();
                                if (val) {
                                  setDetectedLots(prev => prev.map(l => l.id === lot.id
                                    ? { ...l, detectedName: val, fullName: buildLotName(val) } : l));
                                }
                                setEditingNameId(null);
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                              data-testid={`edit-name-${i}`}
                            />
                          ) : (
                            <div className="flex items-center gap-1">
                              <p className="text-sm font-medium text-gray-900 truncate">{lot.fullName}</p>
                              <button onClick={(e) => { e.stopPropagation(); setEditingNameId(lot.id); }}
                                className="shrink-0 text-gray-400 hover:text-blue-500" data-testid={`rename-lot-${i}`}>
                                <Pencil className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-gray-500">{lot.typology}</span>
                            <span className="text-xs text-gray-400">·</span>
                            <span className="text-xs text-gray-500">{lot.surface} m²</span>
                          </div>
                        </div>
                        <div className="w-3 h-3 rounded-full shrink-0 mt-1.5" style={{ backgroundColor: lot.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Import section */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1.5">Projet cible</label>
                <Select value={targetProject || 'none'} onValueChange={(v) => v !== 'none' && setTargetProject(v)}>
                  <SelectTrigger data-testid="import-project-select"><SelectValue placeholder="Choisir un projet" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Choisir un projet</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={handleImport}
                disabled={importing || selectedCount === 0 || !targetProject}
                data-testid="import-lots-btn"
              >
                {importing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Import en cours...</>
                ) : (
                  <><Check className="w-4 h-4 mr-2" />Importer {selectedCount} lot{selectedCount > 1 ? 's' : ''} dans mes projets</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default SmartExtractorPage;
